/**
 * Technical demo vocabulary — linear infrastructure (road + utilities).
 * Each layer holds tagged assets (pipes, ancillaries, furniture, ITS).
 */

export type DemoLayerCategory = 'road' | 'utility' | 'lrs' | 'terrain';

export interface DemoLayerDef {
  id: string;
  label: string;
  group: string;
  category: DemoLayerCategory;
  paletteId: string;
  zIndex: number;
  description: string;
}

/** Road corridor layers (stacked cross-section on one logical group). */
export const DEMO_ROAD_LAYERS: readonly DemoLayerDef[] = [
  {
    id: 'road_cross_section',
    label: 'Pavement · Carriageway · Shoulder',
    group: 'Road Infrastructure',
    category: 'road',
    paletteId: 'road_survey',
    zIndex: 20,
    description: 'Stacked polygons — shoulder (brown), carriageway, pavement, lane separators',
  },
  {
    id: 'drainage_system',
    label: 'Drainage System',
    group: 'Road Infrastructure',
    category: 'road',
    paletteId: 'utilities',
    zIndex: 18,
    description: 'Storm drains, culverts, side drains along corridor',
  },
  {
    id: 'road_furniture',
    label: 'Road Furniture',
    group: 'Road Infrastructure',
    category: 'road',
    paletteId: 'infrastructure',
    zIndex: 22,
    description: 'Signs, lighting, crash barriers, km stones',
  },
  {
    id: 'its_system',
    label: 'ITS',
    group: 'Road Infrastructure',
    category: 'road',
    paletteId: 'default',
    zIndex: 24,
    description: 'CCTV, VMS, ATMS, SOS, weigh-in-motion',
  },
] as const;

/** Utility network layers — transmission + distribution + ancillaries per sector. */
export const DEMO_UTILITY_LAYERS: readonly DemoLayerDef[] = [
  {
    id: 'water_network',
    label: 'Water',
    group: 'Utility Networks',
    category: 'utility',
    paletteId: 'utilities',
    zIndex: 14,
    description: 'Trunk mains, distribution, PRVs, hydrants',
  },
  {
    id: 'telecom_network',
    label: 'Telecom',
    group: 'Utility Networks',
    category: 'utility',
    paletteId: 'utilities',
    zIndex: 15,
    description: 'OFC backbone, distribution, splice pits, cabinets',
  },
  {
    id: 'electrical_network',
    label: 'Electrical',
    group: 'Utility Networks',
    category: 'utility',
    paletteId: 'utilities',
    zIndex: 16,
    description: 'HT transmission, LT distribution, transformers, poles',
  },
  {
    id: 'gas_network',
    label: 'Gas',
    group: 'Utility Networks',
    category: 'utility',
    paletteId: 'utilities',
    zIndex: 17,
    description: 'Transmission pipeline, distribution, valves, regulators',
  },
] as const;

export const DEMO_ALL_LAYERS: readonly DemoLayerDef[] = [
  ...DEMO_ROAD_LAYERS,
  ...DEMO_UTILITY_LAYERS,
];

/** Cross-section colours (shoulder = brown per demo brief). */
export const DEMO_ROAD_COLORS = {
  shoulder: '#8B4513',
  shoulderEdge: '#A0522D',
  carriageway: '#4a5568',
  pavement: '#f5d27a',
  pavementAlt: '#e8c468',
  laneDivider: '#ffffff',
  median: '#6b4f35',
} as const;

export const DEMO_UTILITY_COLORS: Record<string, { main: string; dist: string; ancillary: string }> = {
  water_network: { main: '#0284c7', dist: '#38bdf8', ancillary: '#0ea5e9' },
  telecom_network: { main: '#ea580c', dist: '#fb923c', ancillary: '#f97316' },
  electrical_network: { main: '#eab308', dist: '#facc15', ancillary: '#ca8a04' },
  gas_network: { main: '#b45309', dist: '#d97706', ancillary: '#92400e' },
};

export const CHAINAGE_INTERVAL_OPTIONS_M = [50, 100] as const;

/** OpenTopoMap — terrain relief overlay for surrounding DEM-style context. */
export const DEMO_TERRAIN_TILE_URL = 'https://tile.opentopomap.org/{z}/{x}/{y}.png';
